import PropTypes from 'prop-types';
import s from './MultiRangeSlider.module.scss';
import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import {
    formatValue,
    formatNumberWithSuffix,
    formatNumberWithSuffixAsString,
} from '../../utils/helpers';

const MultiRangeSlider = ({
    min,
    max,
    minVal,
    maxVal,
    onChange = () => {},
    valueText = '',
    formatValues = false,
    // The ends of the range, printed under the track. A caller whose values
    // are read somewhere better — a pair of date fields above the slider —
    // switches them off.
    showValues = true,
}) => {
    const minValRef = useRef(null);
    const maxValRef = useRef(null);
    const range = useRef(null);

    const getPercent = useCallback(
        (value) => Math.round(((value - min) / (max - min)) * 100),
        [min, max]
    );

    useEffect(() => {
        if (maxValRef.current) {
            const minPercent = getPercent(minVal);
            const maxPercent = getPercent(+maxValRef.current.value);

            if (range.current) {
                range.current.style.left = `${minPercent}%`;
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [minVal, getPercent]);

    useEffect(() => {
        if (minValRef.current) {
            const minPercent = getPercent(+minValRef.current.value);
            const maxPercent = getPercent(maxVal);

            if (range.current) {
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [maxVal, getPercent]);

    return (
        <div
            className={classNames(s.Root, {
                [s['Root--Bare']]: !showValues,
            })}>
            <input
                type="range"
                min={min}
                max={max}
                value={minVal}
                ref={minValRef}
                onChange={(event) => {
                    const value = Math.min(+event.target.value, maxVal - 1);
                    onChange({ min: value, max: maxVal });
                    event.target.value = value.toString();
                }}
                className={classNames(s.Thumb, s.Thumb__Zindex3, {
                    [s.Thumb__Zindex5]: minVal > max - 100,
                })}
            />
            <input
                type="range"
                min={min}
                max={max}
                value={maxVal}
                ref={maxValRef}
                onChange={(event) => {
                    const value = Math.max(+event.target.value, minVal + 1);
                    onChange({ min: minVal, max: value });
                    event.target.value = value.toString();
                }}
                className={classNames(s.Thumb, s.Thumb__Zindex4)}
            />

            <div className={s.Slider}>
                <div className={s.Track} />
                <div ref={range} className={s.Range} />
                {!showValues ? null : formatValues ? (
                    <>
                        <div
                            className={
                                s.LeftValue
                            }>{`${formatNumberWithSuffixAsString(minVal)}${valueText}`}</div>
                        <div
                            className={
                                s.RightValue
                            }>{`${formatNumberWithSuffixAsString(maxVal)}${valueText}`}</div>
                    </>
                ) : (
                    <>
                        <div
                            className={
                                s.LeftValue
                            }>{`${minVal}${valueText}`}</div>
                        <div
                            className={
                                s.RightValue
                            }>{`${maxVal}${valueText}`}</div>
                    </>
                )}
            </div>
        </div>
    );
};

MultiRangeSlider.propTypes = {
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    minVal: PropTypes.number.isRequired,
    maxVal: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    valueText: PropTypes.string,
    showValues: PropTypes.bool,
};

export default MultiRangeSlider;
